import { buildTokenContext, isColorValue, resolveColor } from "../../contract/tokens.js";
import {
  createToken,
  deleteToken,
  isLikelyColorCollection,
  listGroups,
  listRows,
  renameToken,
  setTokenValue,
  tokenDisplaySuffix,
  tokenGroupId,
  tokenGroupLabel,
  type TokenCollectionId,
  type TokenRow,
  validateTokenName,
} from "./model.js";

export type TokensPanelCommit = () => void;

type PanelState = {
  collection: TokenCollectionId;
  groupFilter: string | null;
};

const COLLECTION_LABELS: Record<TokenCollectionId, string> = {
  variables: "Variables",
  palette: "Palette",
  semantic: "Semantic",
};

let rootEl: HTMLElement | null = null;
let sidebarEl: HTMLElement | null = null;
let tableWrapEl: HTMLElement | null = null;
let footerEl: HTMLElement | null = null;
let docRef: Record<string, unknown> | null = null;
let onCommit: TokensPanelCommit = () => {};
let readTheme: () => "Light" | "Dark" = () => "Dark";
let state: PanelState = { collection: "variables", groupFilter: null };

export function initTokensPanel(
  container: HTMLElement,
  options: { onCommit: TokensPanelCommit; getTheme: () => "Light" | "Dark" },
): void {
  rootEl = container;
  onCommit = options.onCommit;
  readTheme = options.getTheme;

  rootEl.className = "tokens-panel";
  rootEl.replaceChildren();

  sidebarEl = document.createElement("aside");
  sidebarEl.className = "tokens-panel__sidebar";

  const main = document.createElement("div");
  main.className = "tokens-panel__main";

  tableWrapEl = document.createElement("div");
  tableWrapEl.className = "tokens-panel__table-wrap";

  footerEl = document.createElement("footer");
  footerEl.className = "tokens-panel__footer";

  main.append(tableWrapEl, footerEl);
  rootEl.append(sidebarEl, main);
}

export function updateTokensPanel(doc: Record<string, unknown> | null): void {
  docRef = doc;
  if (!rootEl || !sidebarEl || !tableWrapEl || !footerEl) return;
  if (!doc) {
    rootEl.hidden = false;
    rootEl.replaceChildren();
    const empty = document.createElement("p");
    empty.className = "tokens-panel__loading";
    empty.textContent = "Загрузка контракта…";
    rootEl.append(empty);
    return;
  }
  renderSidebar();
  renderTable();
  renderFooter();
}

function renderSidebar(): void {
  if (!sidebarEl || !docRef) return;
  sidebarEl.replaceChildren();

  const collectionsTitle = document.createElement("div");
  collectionsTitle.className = "tokens-panel__section-title";
  collectionsTitle.textContent = "Collections";
  sidebarEl.append(collectionsTitle);

  const collectionsNav = document.createElement("nav");
  collectionsNav.className = "tokens-panel__nav";
  collectionsNav.setAttribute("aria-label", "Коллекции токенов");

  for (const id of ["variables", "palette", "semantic"] as const) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tokens-panel__nav-btn";
    if (state.collection === id) btn.classList.add("is-active");
    btn.textContent = COLLECTION_LABELS[id];
    btn.addEventListener("click", () => {
      state = { collection: id, groupFilter: null };
      renderSidebar();
      renderTable();
      renderFooter();
    });
    collectionsNav.append(btn);
  }
  sidebarEl.append(collectionsNav);

  const groupsTitle = document.createElement("div");
  groupsTitle.className = "tokens-panel__section-title";
  groupsTitle.textContent = "Groups";
  sidebarEl.append(groupsTitle);

  const groupsNav = document.createElement("nav");
  groupsNav.className = "tokens-panel__nav";
  groupsNav.setAttribute("aria-label", "Группы токенов");

  const allBtn = document.createElement("button");
  allBtn.type = "button";
  allBtn.className = "tokens-panel__nav-btn";
  if (state.groupFilter === null) allBtn.classList.add("is-active");
  const allCount = listRows(docRef, state.collection, null).length;
  allBtn.textContent = `All (${allCount})`;
  allBtn.addEventListener("click", () => {
    state = { ...state, groupFilter: null };
    renderSidebar();
    renderTable();
  });
  groupsNav.append(allBtn);

  for (const group of listGroups(docRef, state.collection)) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tokens-panel__nav-btn";
    if (state.groupFilter === group.id) btn.classList.add("is-active");
    btn.textContent = `${group.label} (${group.count})`;
    btn.addEventListener("click", () => {
      state = { ...state, groupFilter: group.id };
      renderSidebar();
      renderTable();
    });
    groupsNav.append(btn);
  }
  sidebarEl.append(groupsNav);
}

function createColorChit(cssColor: string | null): HTMLSpanElement {
  const chit = document.createElement("span");
  chit.className = "tokens-color-chit";
  chit.setAttribute("aria-hidden", "true");
  const fill = document.createElement("span");
  fill.className = "tokens-color-chit__fill";
  if (cssColor) {
    fill.style.backgroundColor = cssColor;
  }
  chit.append(fill);
  return chit;
}

function resolvedColorCss(raw: string, theme: "Light" | "Dark"): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!docRef?.tokens) {
    return isColorValue(trimmed) ? trimmed : null;
  }
  const ctx = buildTokenContext(docRef.tokens as Record<string, unknown>, theme);
  const resolved = resolveColor(trimmed, ctx as Record<string, string>);
  if (typeof resolved === "string" && resolved.trim() && isColorValue(resolved.trim())) {
    return resolved.trim();
  }
  if (isColorValue(trimmed)) return trimmed;
  return null;
}

function valueCell(
  raw: string,
  theme: "Light" | "Dark",
  onApply: (next: string) => void,
  showColorChit: boolean,
): HTMLTableCellElement {
  const td = document.createElement("td");
  td.className = "tokens-table__value-cell";

  const rowInner = document.createElement("div");
  rowInner.className = "tokens-table__value-inner";

  if (showColorChit) {
    rowInner.append(createColorChit(resolvedColorCss(raw, theme)));
  }

  const input = document.createElement("input");
  input.type = "text";
  input.className = "tokens-table__input";
  input.value = raw;
  input.spellcheck = false;
  input.addEventListener("change", () => {
    onApply(input.value);
    onCommit();
    updateTokensPanel(docRef);
  });
  rowInner.append(input);
  td.append(rowInner);
  return td;
}

function renderTable(): void {
  if (!tableWrapEl || !docRef) return;
  tableWrapEl.replaceChildren();

  const table = document.createElement("table");
  table.className = "tokens-table";

  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  const nameTh = document.createElement("th");
  nameTh.textContent = "Name";
  headRow.append(nameTh);

  if (state.collection === "semantic") {
    for (const label of ["Light", "Dark"]) {
      const th = document.createElement("th");
      th.textContent = label;
      headRow.append(th);
    }
  } else {
    const th = document.createElement("th");
    th.textContent = "Value";
    headRow.append(th);
  }

  const actionsTh = document.createElement("th");
  actionsTh.className = "tokens-table__actions-head";
  actionsTh.textContent = "";
  headRow.append(actionsTh);
  thead.append(headRow);
  table.append(thead);

  const tbody = document.createElement("tbody");
  const rows = listRows(docRef, state.collection, state.groupFilter);
  const showGroupHeaders = state.groupFilter === null;
  let lastGroupId: string | null = null;
  const colorish = isLikelyColorCollection(state.collection);

  for (const row of rows) {
    if (showGroupHeaders) {
      const gid = tokenGroupId(row.name, state.collection);
      if (gid !== lastGroupId) {
        lastGroupId = gid;
        const groupRow = document.createElement("tr");
        groupRow.className = "tokens-table__group-row";
        const td = document.createElement("td");
        td.colSpan = state.collection === "semantic" ? 4 : 3;
        td.textContent = tokenGroupLabel(row.name, state.collection);
        groupRow.append(td);
        tbody.append(groupRow);
      }
    }

    tbody.append(buildDataRow(row, colorish));
  }

  if (rows.length === 0) {
    const empty = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = state.collection === "semantic" ? 4 : 3;
    td.className = "tokens-table__empty";
    td.textContent = "Нет токенов в этой группе";
    empty.append(td);
    tbody.append(empty);
  }

  table.append(tbody);
  tableWrapEl.append(table);
}

function buildDataRow(row: TokenRow, colorish: boolean): HTMLTableRowElement {
  const tr = document.createElement("tr");
  tr.className = "tokens-table__row";

  const nameTd = document.createElement("td");
  nameTd.className = "tokens-table__name-cell";
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "tokens-table__input tokens-table__input--name";
  nameInput.value = row.name;
  nameInput.title = row.name;
  nameInput.dataset.fullName = row.name;
  const suffix = tokenDisplaySuffix(row.name);
  nameInput.placeholder = suffix;
  nameInput.addEventListener("change", () => {
    if (!docRef) return;
    const next = nameInput.value.trim();
    const err = renameToken(docRef, state.collection, row.name, next);
    if (err) {
      nameInput.value = row.name;
      window.alert(err);
      return;
    }
    onCommit();
    updateTokensPanel(docRef);
  });
  nameTd.append(nameInput);
  tr.append(nameTd);

  if (row.kind === "semantic") {
    tr.append(
      valueCell(row.light, "Light", (v) => setTokenValue(docRef!, state.collection, row.name, "Light", v), colorish),
      valueCell(row.dark, "Dark", (v) => setTokenValue(docRef!, state.collection, row.name, "Dark", v), colorish),
    );
  } else {
    tr.append(
      valueCell(
        row.value,
        readTheme(),
        (v) => setTokenValue(docRef!, state.collection, row.name, "value", v),
        colorish,
      ),
    );
  }

  const actionsTd = document.createElement("td");
  actionsTd.className = "tokens-table__actions";
  const del = document.createElement("button");
  del.type = "button";
  del.className = "tokens-table__delete";
  del.title = "Удалить";
  del.textContent = "×";
  del.addEventListener("click", () => {
    if (!docRef) return;
    if (!window.confirm(`Удалить токен «${row.name}»?`)) return;
    deleteToken(docRef, state.collection, row.name);
    onCommit();
    updateTokensPanel(docRef);
  });
  actionsTd.append(del);
  tr.append(actionsTd);

  return tr;
}

function renderFooter(): void {
  if (!footerEl || !docRef) return;
  footerEl.replaceChildren();

  const form = document.createElement("div");
  form.className = "tokens-panel__new";

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "tokens-table__input";
  nameInput.placeholder = "Имя токена";
  nameInput.setAttribute("aria-label", "Имя нового токена");

  const valueInput = document.createElement("input");
  valueInput.type = "text";
  valueInput.className = "tokens-table__input";
  valueInput.placeholder = state.collection === "semantic" ? "Значение (Light и Dark)" : "Значение";
  valueInput.setAttribute("aria-label", "Значение нового токена");

  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "tokens-panel__add-btn";
  addBtn.textContent = "Новый токен";

  addBtn.addEventListener("click", () => {
    if (!docRef) return;
    const name = nameInput.value.trim();
    const value = valueInput.value;
    const nameErr = validateTokenName(name, state.collection, docRef);
    if (nameErr) {
      window.alert(nameErr);
      nameInput.focus();
      return;
    }
    const createErr = createToken(docRef, state.collection, name, value);
    if (createErr) {
      window.alert(createErr);
      return;
    }
    nameInput.value = "";
    valueInput.value = "";
    onCommit();
    updateTokensPanel(docRef);
  });

  form.append(nameInput, valueInput, addBtn);
  footerEl.append(form);
}
